import { useQueryClient, useSuspenseQuery } from '@tanstack/react-query';

import { getRequest } from '@/api/rest/hooks/requests/get';
import {
  useDeleteDashboardIndicatorProductSubscriptionConfiguration,
  usePatchDashboardIndicatorProductSubscriptionConfiguration,
  usePostDashboardIndicatorProductSubscriptionConfiguration,
  usePostIndicatorProductSubscriptionConfigurationFileUpload,
} from '@/api/rest/resources/indicatorProduct';
import {
  DeleteDashboardIndicatorProductSubscriptionConfigurationRequestQueryParams,
  GET_INDICATOR_PRODUCT_SUBSCRIPTION_CONFIGURATION_BY_ID_PATH,
  GET_INDICATOR_PRODUCT_SUBSCRIPTION_CONFIGURATIONS_PATH,
  GetDashboardIndicatorProductSubscriptionConfigurationByIdErrorDto,
  GetDashboardIndicatorProductSubscriptionConfigurationByIdRequestPathVariables,
  GetDashboardIndicatorProductSubscriptionConfigurationByIdRequestQueryParams,
  GetDashboardIndicatorProductSubscriptionConfigurationByIdSuccessDto,
  GetIndicatorProductSubscriptionConfigurationsErrorDto,
  GetIndicatorProductSubscriptionConfigurationsRequestPathVariables,
  GetIndicatorProductSubscriptionConfigurationsRequestQueryParams,
  GetIndicatorProductSubscriptionConfigurationsSuccessDto,
  INDICATOR_PRODUCT_SUBSCRIPTION_CONFIGURATION_TYPE,
  PatchDashboardIndicatorProductSubscriptionConfigurationBodyData,
  PatchDashboardIndicatorProductSubscriptionConfigurationRequestQueryParams,
  PostDashboardIndicatorProductSubscriptionConfigurationBodyData,
} from '@/api/rest/resources/types/IndicatorProduct';
import { useOrganizationId } from '@/pages/shared/hooks/useOrganizationId';
import { assertIsDefined } from '@/utils/assert';

const QUERY_KEY_BASE = 'indicator-product-subscription-configuration';

export const useGetIndicatorProductSubscriptionConfigurations = (
  organizationIdArg?: string,
  fileInfoLatest = true,
  filters: Pick<GetIndicatorProductSubscriptionConfigurationsRequestQueryParams, 'status' | 'type'> = {},
) => {
  const orgId = useOrganizationId();
  const organizationId = organizationIdArg ?? orgId;

  assertIsDefined(organizationId, 'organizationId');

  const queryParams: GetIndicatorProductSubscriptionConfigurationsRequestQueryParams = {
    organization_id: organizationId,
    file_info_latest: fileInfoLatest,
    ...filters,
  };

  const queryFn = async () => {
    return getRequest<
      GetIndicatorProductSubscriptionConfigurationsSuccessDto,
      GetIndicatorProductSubscriptionConfigurationsRequestPathVariables,
      GetIndicatorProductSubscriptionConfigurationsRequestQueryParams
    >(GET_INDICATOR_PRODUCT_SUBSCRIPTION_CONFIGURATIONS_PATH, undefined, queryParams);
  };

  return useSuspenseQuery<
    GetIndicatorProductSubscriptionConfigurationsSuccessDto,
    GetIndicatorProductSubscriptionConfigurationsErrorDto
  >({
    queryKey: ['organization', organizationId, QUERY_KEY_BASE, filters],
    queryFn,
    staleTime: 1000 * 60 * 5,
  });
};

export const useGetIndicatorProductSubscriptionConfigurationById = (
  ipSubConfigurationId: string,
  organizationIdArg?: string,
) => {
  const orgId = useOrganizationId();
  const organizationId = organizationIdArg ?? orgId;

  assertIsDefined(ipSubConfigurationId, 'ipSubConfigurationId');
  assertIsDefined(organizationId, 'organizationId');

  const pathVariables: GetDashboardIndicatorProductSubscriptionConfigurationByIdRequestPathVariables = {
    id: ipSubConfigurationId,
  };

  const queryParams: GetDashboardIndicatorProductSubscriptionConfigurationByIdRequestQueryParams = {
    organization_id: organizationId,
  };

  const queryFn = async () => {
    return getRequest<
      GetDashboardIndicatorProductSubscriptionConfigurationByIdSuccessDto,
      GetDashboardIndicatorProductSubscriptionConfigurationByIdRequestPathVariables,
      GetDashboardIndicatorProductSubscriptionConfigurationByIdRequestQueryParams
    >(GET_INDICATOR_PRODUCT_SUBSCRIPTION_CONFIGURATION_BY_ID_PATH, pathVariables, queryParams);
  };

  return useSuspenseQuery<
    GetDashboardIndicatorProductSubscriptionConfigurationByIdSuccessDto,
    GetDashboardIndicatorProductSubscriptionConfigurationByIdErrorDto
  >({
    queryKey: [QUERY_KEY_BASE, pathVariables, queryParams],
    queryFn,
    staleTime: 1000 * 60 * 5,
  });
};

export const useCreateIndicatorProductSubscriptionConfiguration = () => {
  const mutation = usePostDashboardIndicatorProductSubscriptionConfiguration();
  const queryClient = useQueryClient();
  const orgId = useOrganizationId();

  const submit = async (
    bodyDataArgs: Partial<PostDashboardIndicatorProductSubscriptionConfigurationBodyData>,
    onSuccess?: () => void,
    onError?: (error: Error) => void,
  ) => {
    const organizationId = bodyDataArgs?.organization ?? orgId;
    assertIsDefined(organizationId, 'organizationId');
    assertIsDefined(bodyDataArgs?.status, 'status');
    assertIsDefined(bodyDataArgs?.indicator_name, 'indicator_name');
    assertIsDefined(bodyDataArgs?.type, 'type');

    if (bodyDataArgs?.type === INDICATOR_PRODUCT_SUBSCRIPTION_CONFIGURATION_TYPE.DEFAULT) {
      assertIsDefined(bodyDataArgs?.aggregation_method, 'aggregation_method');
    }

    const bodyData = new FormData();
    Object.entries(bodyDataArgs).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        bodyData.append(key, value);
      }
    });
    bodyData.append('organization', organizationId);

    await mutation.mutateAsync(
      {
        pathVariables: undefined,
        queryParams: undefined,
        bodyData: bodyData as unknown as PostDashboardIndicatorProductSubscriptionConfigurationBodyData,
      },
      {
        onSuccess: () => {
          queryClient.invalidateQueries({ queryKey: ['organization', organizationId, QUERY_KEY_BASE] });
          onSuccess?.();
        },
        onError: (error) => {
          onError?.(error);
        },
      },
    );
  };

  return { submit, isSubmitting: mutation.isPending };
};

export const useUpdateIndicatorProductSubscriptionConfiguration = () => {
  const mutation = usePatchDashboardIndicatorProductSubscriptionConfiguration();
  const queryClient = useQueryClient();
  const orgId = useOrganizationId();

  const submit = async (
    id: string,
    bodyData: PatchDashboardIndicatorProductSubscriptionConfigurationBodyData,
    onSuccess?: () => void,
    onError?: (error: Error) => void,
  ) => {
    assertIsDefined(id, 'id');
    assertIsDefined(orgId, 'organizationId');

    const queryParams: PatchDashboardIndicatorProductSubscriptionConfigurationRequestQueryParams = {
      organization_id: orgId,
    };

    await mutation.mutateAsync(
      { pathVariables: { id }, queryParams, bodyData },
      {
        onSuccess: () => {
          queryClient.invalidateQueries({ queryKey: ['organization', orgId, QUERY_KEY_BASE] });
          onSuccess?.();
        },
        onError: (error) => {
          onError?.(error);
        },
      },
    );
  };

  return { submit, isSubmitting: mutation.isPending };
};

export const useRemoveIndicatorProductSubscriptionConfiguration = () => {
  const mutation = useDeleteDashboardIndicatorProductSubscriptionConfiguration();
  const queryClient = useQueryClient();
  const orgId = useOrganizationId();

  const submit = async (id: string, onSuccess?: () => void, onError?: (error: Error) => void) => {
    assertIsDefined(id, 'id');
    assertIsDefined(orgId, 'organizationId');

    const queryParams: DeleteDashboardIndicatorProductSubscriptionConfigurationRequestQueryParams = {
      organization_id: orgId,
    };

    await mutation.mutateAsync(
      { pathVariables: { id }, queryParams },
      {
        onSuccess: () => {
          queryClient.invalidateQueries({ queryKey: ['organization', orgId, QUERY_KEY_BASE] });
          onSuccess?.();
        },
        onError: (error) => {
          onError?.(error);
        },
      },
    );
  };

  return { submit, isSubmitting: mutation.isPending };
};

export const useUploadInSituDataFile = () => {
  const queryClient = useQueryClient();
  const orgId = useOrganizationId();
  const mutation = usePostIndicatorProductSubscriptionConfigurationFileUpload();

  const submit = async (
    { file, configurationId, description }: { file: File; configurationId: string; description?: string },
    onSuccess?: () => void,
    onError?: (error: Error) => void,
  ) => {
    assertIsDefined(file, 'file');
    assertIsDefined(configurationId, 'IP subscription configuration ID');
    assertIsDefined(orgId, 'organizationId');

    const formData = new FormData();
    formData.append('file_info', file);
    formData.append('indicator_product_subscription_configuration_id', configurationId);
    if (description) {
      formData.append('description', description);
    }

    await mutation.mutateAsync(
      {
        queryParams: { organization_id: orgId },
        bodyData: formData,
      },
      {
        onSuccess: () => {
          queryClient.invalidateQueries({ queryKey: ['organization', orgId, QUERY_KEY_BASE] });
          onSuccess?.();
        },
        onError: (error) => {
          onError?.(error);
        },
      },
    );
  };

  return { submit, isSubmitting: mutation.isPending };
};
